import React, { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';

type Point = [number, number, number];
const COUNT = 1800;
const MORPH_SECONDS = 4;
const STAGE_INTERVAL_MS = 8500;
const STAGES = [
  { name: 'Scattered information', title: 'Information is everywhere.', description: 'Documents, data, observations, and ideas are spread across different sources. We bring them together.' },
  { name: 'AI model', title: 'From scattered information to intelligence.', description: 'We turn scattered information into intelligence, bringing knowledge together to build AI models.' },
];
const NEURAL_LAYERS = [
  { count: 180, columns: 15, scale: .38, start: 0 },
  { count: 360, columns: 20, scale: .7, start: 180 },
  { count: 720, columns: 30, scale: 1, start: 540 },
  { count: 360, columns: 20, scale: .7, start: 1260 },
  { count: 180, columns: 15, scale: .38, start: 1620 },
];
const NEURAL_EDGES = NEURAL_LAYERS.slice(0, -1).flatMap((source, layer) => {
  const target = NEURAL_LAYERS[layer + 1];
  const edges: { from: number; to: number; layer: number }[] = [];
  const seen = new Set<string>();
  const connect = (from: number, to: number) => {
    const key = `${from}-${to}`;
    if (!seen.has(key)) { seen.add(key); edges.push({ from, to, layer }); }
  };
  // Cover every neuron when expanding and contracting between unequal layers.
  for (let i = 0; i < source.count; i++) {
    const destination = Math.floor((i + .5) / source.count * target.count);
    connect(source.start + i, target.start + destination);
    connect(source.start + i, target.start + (destination + 43) % target.count);
  }
  for (let i = 0; i < target.count; i++) {
    connect(source.start + Math.floor((i + .5) / target.count * source.count), target.start + i);
  }
  return edges;
});

// Two states, one persistent set of points: scattered information becomes model neurons.
function createField() {
  let seed = 71;
  const random = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
  const gaussian = () => Math.sqrt(-2 * Math.log(Math.max(random(), 1e-8))) * Math.cos(2 * Math.PI * random());
  const pockets: Point[] = [[-65, -32, 18], [58, 18, -38], [12, -48, 52], [-20, 48, -20], [36, 30, 54]];
  return Array.from({ length: COUNT }, (_, i) => {
    const group = i % 4;
    const pocket = pockets[Math.floor(random() * pockets.length)];
    let scattered: Point;
    // Overlapping, warped Gaussian pockets create uneven density and soft, irregular edges.
    // Reject only distant outliers so the cloud remains inside the rotating view.
    do {
      const x = pocket[0] + gaussian() * 48;
      const y = pocket[1] + gaussian() * 33;
      const z = pocket[2] + gaussian() * 42;
      scattered = [x + 22 * Math.sin(y * .033), y + 16 * Math.sin(z * .035), z + 18 * Math.sin(x * .025)];
    } while (Math.hypot(scattered[0], scattered[1] * 1.4, scattered[2]) > 205);
    const layer = NEURAL_LAYERS.findIndex(item => i < item.start + item.count);
    const layout = NEURAL_LAYERS[layer];
    const local = i - layout.start;
    const rows = layout.count / layout.columns;
    const row = Math.floor(local / layout.columns);
    const column = local % layout.columns;
    const neural: Point = [
      (layer - 2) * 80,
      (row / (rows - 1) - .5) * 210 * layout.scale,
      (column / (layout.columns - 1) - .5) * 210 * layout.scale,
    ];
    return { group, layer, positions: [scattered, neural], weight: .65 + random() * .7 };
  });
}
const PARTICLES = createField();

function PointCloud({ stage, playing }: { stage: number; playing: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const state = useRef({ stage, playing });
  useEffect(() => { state.current = { stage, playing }; }, [stage, playing]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const positions = PARTICLES.map(p => [...p.positions[state.current.stage]] as Point);
    let frame = 0;
    let previous = 0;
    let rotation = 0;
    let signalTime = 0;
    let visible = true;
    let width = 520;
    let height = 360;
    let settling = 0;
    let lastStage = state.current.stage;
    let morphElapsed = MORPH_SECONDS;
    let startPositions = positions.map(p => [...p] as Point);
    let weights: number[] = STAGES.map((_, i) => i === lastStage ? 1 : 0);
    let startWeights = [...weights];
    let ink = '';
    let accent = '';
    let accentRgb = [181, 164, 231];
    const palette = () => {
      const styles = getComputedStyle(document.documentElement);
      ink = styles.getPropertyValue('--ink').trim();
      accent = styles.getPropertyValue('--accent').trim();
      accentRgb = [1, 3, 5].map(offset => parseInt(accent.slice(offset, offset + 2), 16));
      settling = 90;
    };
    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      settling = 90;
    };
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      const dt = previous ? Math.min((now - previous) / 1000, .05) : .016;
      previous = now;
      if (!visible || document.hidden) return;
      const current = state.current;
      if (lastStage !== current.stage) {
        startPositions = positions.map(p => [...p] as Point);
        startWeights = [...weights];
        morphElapsed = 0;
        lastStage = current.stage;
      }
      const morphing = morphElapsed < MORPH_SECONDS;
      const moving = current.playing && !reducedMotion.matches;
      if (!moving && !morphing && settling <= 0) return;
      settling--;
      if (moving) { rotation += dt * .42; signalTime += dt; }
      morphElapsed = reducedMotion.matches ? MORPH_SECONDS : Math.min(MORPH_SECONDS, morphElapsed + dt);
      const progress = morphElapsed / MORPH_SECONDS;
      // Smooth acceleration and deceleration, with no jump at a stage boundary.
      const blend = progress * progress * progress * (progress * (progress * 6 - 15) + 10);
      weights = startWeights.map((weight, i) => weight + ((i === current.stage ? 1 : 0) - weight) * blend);
      const scale = width / 520;
      const angle = -.28 + rotation;
      const projected = positions.map((p, i) => {
        const target = PARTICLES[i].positions[current.stage];
        for (let axis = 0; axis < 3; axis++) p[axis] = startPositions[i][axis] + (target[axis] - startPositions[i][axis]) * blend;
        const x = p[0] * Math.cos(angle) + p[2] * Math.sin(angle);
        const z = -p[0] * Math.sin(angle) + p[2] * Math.cos(angle);
        const y = p[1] * .82 - z * .12;
        const perspective = 1000 / (1000 - z);
        return { x: width / 2 + x * perspective * scale, y: height / 2 + y * perspective * scale, z, i };
      });
      context.clearRect(0, 0, width, height);
      // Connections fade in as individual particles settle into neuron layers.
      const networkOpacity = weights[1] * weights[1];
      // A continuous color wave travels through every connection. No extra signal objects.
      const blue = [67, 128, 181];
      const layerColors = NEURAL_LAYERS.map((_, layer) => {
        const wave = Math.pow(.5 + .5 * Math.cos(signalTime * 1.35 - layer * 1.2), 3);
        const channels = accentRgb.map((channel, i) => Math.round(channel + (blue[i] - channel) * wave));
        return `rgb(${channels.join(',')})`;
      });
      for (const edge of NEURAL_EDGES) {
        const a = projected[edge.from];
        const b = projected[edge.to];
        const flow = context.createLinearGradient(a.x, a.y, b.x, b.y);
        flow.addColorStop(0, layerColors[edge.layer]);
        flow.addColorStop(1, layerColors[edge.layer + 1]);
        context.strokeStyle = flow;
        const depthOpacity = Math.max(.025, Math.min(.12, .065 + (a.z + b.z) / 4000));
        context.globalAlpha = networkOpacity * depthOpacity;
        context.lineWidth = .5 * scale;
        context.beginPath(); context.moveTo(a.x, a.y); context.lineTo(b.x, b.y); context.stroke();
      }

      const sorted = [...projected].sort((a, b) => a.z - b.z);
      for (const p of sorted) {
        context.fillStyle = PARTICLES[p.i].group === 0 ? ink : accent;
        const opacity = Math.min(.9, Math.max(.18, .5 + p.z / 250)) * [1, 1, .8, .65][PARTICLES[p.i].group];
        const layer = PARTICLES[p.i].layer;
        const activation = .5 + .5 * Math.sin(signalTime * 2.1 - layer * .8);
        context.globalAlpha = Math.min(1, opacity + networkOpacity * activation * .18);
        context.beginPath(); context.arc(p.x, p.y, PARTICLES[p.i].weight * scale, 0, Math.PI * 2); context.fill();
      }
      context.globalAlpha = 1;
    };
    const observer = new ResizeObserver(resize);
    const themeObserver = new MutationObserver(palette);
    const visibilityObserver = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; settling = 90; });
    observer.observe(canvas); visibilityObserver.observe(canvas);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    reducedMotion.addEventListener('change', palette);
    palette(); resize(); frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame); observer.disconnect(); themeObserver.disconnect(); visibilityObserver.disconnect();
      reducedMotion.removeEventListener('change', palette);
    };
  }, []);

  return <canvas ref={canvasRef} className="intelligence-canvas" role="img" aria-label={`${STAGES[stage].title} ${STAGES[stage].description}`} />;
}

export default function ResearchField() {
  const [stage, setStage] = useState(0);
  const [playing, setPlaying] = useState(() => !window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [visible, setVisible] = useState(true);
  const figureRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: .25 });
    if (figureRef.current) observer.observe(figureRef.current);
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stop = () => { if (media.matches) setPlaying(false); };
    media.addEventListener('change', stop);
    return () => { observer.disconnect(); media.removeEventListener('change', stop); };
  }, []);
  useEffect(() => {
    if (!playing || !visible) return;
    const timer = window.setTimeout(() => setStage(value => (value + 1) % STAGES.length), STAGE_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [playing, stage, visible]);

  return (
    <figure ref={figureRef} className="research-field intelligence-field">
      <div className="intelligence-scene">
        <PointCloud stage={stage} playing={playing && visible} />
      </div>
      <div className="field-controls cloud-tabs" role="group" aria-label="Information states">
        <div className="cloud-tab-track">
          <span className="cloud-tab-indicator" aria-hidden="true" style={{ transform: `translateX(${stage * 100}%)` }} />
          {STAGES.map((item, index) => (
            <button key={item.name} aria-label={item.name} aria-pressed={stage === index} onClick={() => setStage(index)}>
              <span className="mono">0{index + 1}</span> {item.name}
            </button>
          ))}
        </div>
      </div>
      <figcaption className="intelligence-caption">
        <div className="cloud-caption-slider" aria-live={playing ? 'off' : 'polite'}>
          {STAGES.map((item, index) => {
            const position = index - stage;
            return <div key={item.name} className="cloud-caption-slide" aria-hidden={stage !== index}
              style={{ transform: `translateX(${position * 108}%)`, opacity: stage === index ? 1 : 0 }}>
              <h3>{item.title}</h3><p>{item.description}</p>
            </div>;
          })}
        </div>
        <div className="intelligence-footer"><span>Collect. Structure. Build intelligence.</span><button aria-label={playing ? 'Pause point cloud animation' : 'Play point cloud animation'} onClick={() => setPlaying(value => !value)}>{playing ? <Pause size={13} /> : <Play size={13} />}<span>{playing ? 'Pause' : 'Play'}</span></button></div>
      </figcaption>
    </figure>
  );
}
